import { useEffect, useCallback } from 'react';
import {
  saveCustomer,
  saveProduct,
  saveBill,
  saveTreasuryTransaction,
  loadAllData
} from '../services/firestoreService';

export const useFirestoreSync = (
  customers,
  products,
  setCustomers,
  setProducts
) => {
  // Load data from Firestore on mount - ALWAYS use Firebase, no localStorage
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('🔄 Loading data from Firestore...');
        const data = await loadAllData();
        
        // Always load customers from Firestore
        if (data.customers.length > 0) {
          console.log(`✅ Loaded ${data.customers.length} customers from Firestore`);
          setCustomers(data.customers);
        } else {
          console.log('ℹ️ No customers found in Firestore');
        }
        
        // Always load products from Firestore
        if (data.products.length > 0) {
          console.log(`✅ Loaded ${data.products.length} products from Firestore`);
          setProducts(data.products);
        } else {
          console.log('ℹ️ No products found in Firestore');
        }
        
        console.log('✅ Data loading complete from Firebase');
      } catch (error) {
        console.error('❌ Error loading data from Firestore:', error.message);
        console.error('Full error:', error);
      }
    };

    loadData();
  }, [setCustomers, setProducts]);

  // Sync customers to Firestore whenever they change
  useEffect(() => {
    const syncCustomers = async () => {
      if (customers.length > 0) {
        try {
          console.log(`🔄 Syncing ${customers.length} customers to Firestore...`);
          for (const customer of customers) {
            await saveCustomer(customer);
          }
          console.log('✅ All customers synced to Firestore');
        } catch (error) {
          console.error('❌ Error syncing customers:', error.message);
        }
      }
    };

    // Debounce the sync to avoid too many writes
    const timeoutId = setTimeout(syncCustomers, 1000);
    return () => clearTimeout(timeoutId);
  }, [customers]);

  // Sync products to Firestore whenever they change
  useEffect(() => {
    const syncProducts = async () => {
      try {
        if (products.length > 0) {
          console.log(`🔄 Syncing ${products.length} products to Firestore...`);
          for (const product of products) {
            await saveProduct(product);
          }
          console.log('✅ All products synced to Firestore');
        } else {
          console.log('ℹ️ No products to sync (empty array)');
        }
      } catch (error) {
        console.error('❌ Error syncing products:', error.message);
      }
    };

    // Debounce the sync to avoid too many writes
    const timeoutId = setTimeout(syncProducts, 1000);
    return () => clearTimeout(timeoutId);
  }, [products]);

  // Function to save a bill
  const saveBillToFirestore = useCallback(async (bill) => {
    try {
      const result = await saveBill(bill);
      if (result.success) {
        console.log('Bill saved to Firestore');
      }
      return result;
    } catch (error) {
      console.error('Error saving bill:', error);
      return { success: false, error };
    }
  }, []);

  // Function to save treasury transaction
  const saveTreasuryToFirestore = useCallback(async (transaction) => {
    try {
      const result = await saveTreasuryTransaction(transaction);
      if (result.success) {
        console.log('Treasury transaction saved to Firestore');
      }
      return result;
    } catch (error) {
      console.error('Error saving treasury transaction:', error);
      return { success: false, error };
    }
  }, []);

  return {
    saveBillToFirestore,
    saveTreasuryToFirestore
  };
};
